import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { errMsg, formatDate, timeAgo, useApi } from "@/lib/api";

export function TrashList({ refreshKey, onChanged }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [purging, setPurging] = useState(null);
  const load = useCallback(() => api.get("/trash").then(({ data }) => setItems(data)).catch(() => setItems([])), [api]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const restore = async (p) => {
    try { await api.post(`/trash/${p.id}/restore`); toast.success(`"${p.name}" ripristinato`); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const purge = async () => {
    const p = purging; setPurging(null);
    try { await api.delete(`/trash/${p.id}`); toast.success("Eliminato definitivamente"); onChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <ul className="divide-y divide-slate-100" data-testid="trash-list">
        {items === null && <li className="py-3 text-sm text-slate-500">Caricamento…</li>}
        {items?.length === 0 && <li className="py-3 text-sm text-slate-500" data-testid="trash-empty">Il cestino è vuoto</li>}
        {items?.map((p) => (
          <li key={p.id} className="py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`trash-item-${p.id}`}>
            <div className="min-w-0 flex-1 basis-full sm:basis-0">
              <p className="font-medium text-slate-800 truncate">{p.name}</p>
              <p className="text-xs text-slate-500">Eliminato {timeAgo(p.deleted_at)} · eliminazione definitiva il {formatDate(p.purge_at)}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => restore(p)} data-testid={`trash-restore-${p.id}`}><RotateCcw size={13} className="mr-1" />Ripristina</Button>
            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setPurging(p)} data-testid={`trash-purge-${p.id}`}><Trash2 size={13} className="mr-1" />Elimina definitivamente</Button>
          </li>
        ))}
      </ul>
      <ConfirmDialog open={!!purging} onOpenChange={(o) => !o && setPurging(null)} destructive confirmLabel="Elimina definitivamente"
        title={`Eliminare definitivamente "${purging?.name}"?`} description="Progetto, versioni e cronologia AI verranno cancellati. Resterà recuperabile solo dai backup giornalieri (30 giorni)." onConfirm={purge} />
    </>
  );
}

export function ImportProject({ onImported }) {
  const api = useApi();
  const ref = useRef(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    try { const { data } = await api.post("/projects/import", fd); toast.success(`Importato "${data.name}"`); setName(""); onImported?.(data); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome del nuovo progetto (facoltativo)" className="sm:max-w-xs" data-testid="import-name-input" />
      <input ref={ref} type="file" accept=".zip,.json,.gz" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
      <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="import-project-btn">
        {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}Importa file ZIP / JSON
      </Button>
    </div>
  );
}
